/**
 * wayai sync-skills [--connection-id <uuid>] — sync skills to Anthropic/OpenAI provider connections
 *
 * Triggers provider-side skill sync for the hub specified in .wayai.yaml.
 * Useful after `wayai push` when the hub uses native skill integration (Anthropic or OpenAI).
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import type { SyncSkillsResponse, SyncSkillsResultItem } from '../lib/types.js';

export function parseArgs(args: string[]): { connectionId: string | undefined } {
  let connectionId: string | undefined;
  const idx = args.indexOf('--connection-id');
  if (idx !== -1 && args[idx + 1]) {
    connectionId = args[idx + 1];
  }
  return { connectionId };
}

function printSyncResults(response: SyncSkillsResponse): void {
  console.log(response.message);

  for (const item of response.results) {
    const parts: string[] = [`${item.connection_name}:`];
    if (item.uploaded > 0) parts.push(`${item.uploaded} uploaded`);
    if (item.failed > 0) parts.push(`${item.failed} failed`);
    if (item.uploaded === 0 && item.failed === 0) parts.push('no changes');
    console.log(`  ${parts.join(' ')}`);

    for (const err of item.errors) {
      console.log(`  \x1b[33m! ${err}\x1b[0m`);
    }
  }
}

export async function syncSkillsCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { connectionId } = parseArgs(args);
  requireRepoConfig();
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  console.log('Syncing skills...');
  const response = await client.syncSkills(hubId, connectionId);
  printSyncResults(response);

  const hasFailed = response.results.some((r: SyncSkillsResultItem) => r.failed > 0);
  if (hasFailed) {
    process.exit(1);
  }
}
